import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  CreateSliderData,
  SLIDER_AUDIENCES,
  SliderAudience,
  SliderRow,
  SlidersApi,
  UpdateSliderData,
  UploadApi,
} from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/format';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

const AUDIENCE_LABEL: Record<string, string> = {
  student: 'Student',
  teacher: 'Teacher',
  all: 'All',
};

const AUDIENCE_TONE: Record<string, 'blue' | 'indigo' | 'slate'> = {
  student: 'blue',
  teacher: 'indigo',
  all: 'slate',
};

/**
 * Sliders — home-screen banner images shown in the student/parent/teacher
 * mobile apps. This is the admin management side; the actual reads it feeds
 * (GET /api/student/sliders, GET /api/parent/sliders) already exist and are
 * real, previously mislabeled-dead legacy data (see migrate-27-sliders.ts).
 * Legacy rows (image not uploaded through this UI, `for_slider` blank or
 * 'teacher') are listed and editable/deletable here like any other row.
 */
export function SlidersPage() {
  const { can } = usePermissions();
  const canWrite = can('/sliders', 'create');
  const canDelete = can('/sliders', 'delete');
  const qc = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SliderRow | null>(null);
  const [deleting, setDeleting] = useState<SliderRow | null>(null);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['sliders'],
    queryFn: SlidersApi.list,
  });

  const remove = useMutation({
    mutationFn: (id: string) => SlidersApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['sliders'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Sliders"
        description="Banner images shown on the student, parent, and teacher app home screens."
        actions={
          canWrite && (
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Slider
            </button>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isFetching ? (
          <div className="col-span-full py-12 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400">
            No sliders yet.
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="aspect-[16/9] w-full bg-slate-100">
                <img src={r.imageUrl} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Badge tone={AUDIENCE_TONE[r.forSlider] ?? 'slate'}>{AUDIENCE_LABEL[r.forSlider] ?? '(legacy — unset)'}</Badge>
                  <span className="text-xs text-slate-400">{formatDate(r.createdAt)}</span>
                </div>
                {r.link ? (
                  <a href={r.link} target="_blank" rel="noreferrer" className="block truncate text-xs text-brand-600 hover:underline">
                    {r.link}
                  </a>
                ) : (
                  <span className="block truncate text-xs text-slate-400">No link</span>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  {canWrite && (
                    <button
                      type="button"
                      title="Edit"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                      onClick={() => setEditing(r)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      title="Delete"
                      className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setDeleting(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Slider" size="md">
        <SliderForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Slider" size="md">
        {editing && <SliderForm slider={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title="Delete slider?"
        message="This will permanently remove this slider and its image. This cannot be undone."
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

function SliderForm({ slider, onDone }: { slider?: SliderRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [imageUrl, setImageUrl] = useState(slider?.imageUrl ?? '');
  const [link, setLink] = useState(slider?.link ?? '');
  const [forSlider, setForSlider] = useState<SliderAudience | ''>((slider?.forSlider as SliderAudience) || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const isEdit = !!slider;

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        const data: UpdateSliderData = { imageUrl: imageUrl || undefined, link, forSlider: forSlider || undefined };
        return SlidersApi.update(slider!.id, data);
      }
      const data: CreateSliderData = { imageUrl, link: link || undefined, forSlider: forSlider as SliderAudience };
      return SlidersApi.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sliders'] });
      onDone();
    },
  });

  async function handleFilePick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      setImageUrl(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  const valid = !!imageUrl && !!forSlider;

  return (
    <div className="space-y-4">
      <Field label="Image" required>
        {imageUrl ? (
          <div className="relative">
            <img src={imageUrl} alt="" className="aspect-[16/9] w-full rounded-md border border-slate-200 object-cover" />
            <label className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-center gap-1.5 rounded-b-md bg-black/50 py-1.5 text-xs text-white hover:bg-black/60">
              <ImagePlus className="h-3.5 w-3.5" /> Replace image
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])}
              />
            </label>
          </div>
        ) : (
          <label className="flex aspect-[16/9] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-slate-300 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-600">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {uploading ? 'Uploading…' : 'Click to upload an image'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])}
            />
          </label>
        )}
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      </Field>

      <Field label="Link" hint="Opened when the banner is tapped — optional.">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
      </Field>

      <Field label="Show to" required>
        <Select value={forSlider} onChange={(e) => setForSlider(e.target.value as SliderAudience)}>
          <option value="">Select audience</option>
          {SLIDER_AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {AUDIENCE_LABEL[a]}
            </option>
          ))}
        </Select>
      </Field>

      {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!valid || save.isPending || uploading} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
        </button>
      </div>
    </div>
  );
}
